import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PageHeader.module.scss';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** The page's actions, right-aligned (language, notifications, buttons). */
  children?: React.ReactNode;
}

/**
 * The header bar of every page outside a project: title and today's date, an
 * optional subtitle, the actions on the right and a separator below - the same
 * bar a project's pages have, so Dashboard, Project Intelligence, Feedback and
 * Templates read as one application.
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, children }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language?.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';
  const today = new Date().toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <header className={styles.header}>
      <div className={styles.text}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          <span className={styles.date}>| {today}</span>
        </div>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {children && <div className={styles.actions}>{children}</div>}
    </header>
  );
};

export default PageHeader;
